import { FaSyncAlt } from 'react-icons/fa';
import { Badge, Dropdown } from '@/components/UI';
import type { AuditProfile, OptimizerJobRow } from './types';
import {
    badgeVariantForJobStatus,
    badgeVariantForOutcomeMetric,
    badgeVariantForWorkerLiveness,
    helpTextForOptionObjective,
    labelForOptionObjective,
    labelForOutcomeMetric,
    labelForWorkerLiveness
} from './helpers';

const AUDIT_PROFILE_OPTIONS: { profile: AuditProfile; label: string; description: string }[] = [
    {
        profile: 'liquid_large_cap_options',
        label: 'Large-cap options',
        description: 'Weights liquidity and large-cap fit for options trading.'
    },
    {
        profile: 'general',
        label: 'General',
        description: 'Profile-neutral scoring across all entities.'
    }
];

interface JobDetailHeaderProps {
    selectedJobId: number;
    selectedJob: OptimizerJobRow | null;
    auditPackPending: boolean;
    onRunAudit: (profile: AuditProfile) => void;
    isRefreshing: boolean;
    onRefresh: () => void;
}

const JobDetailHeader = ({ selectedJobId, selectedJob, auditPackPending, onRunAudit, isRefreshing, onRefresh }: JobDetailHeaderProps) => {
    const workerLivenessLabel = selectedJob ? labelForWorkerLiveness(selectedJob) : null;
    const auditDisabled = selectedJob?.status !== 'completed' || auditPackPending;

    return (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-4">
            <div className="flex flex-wrap items-center gap-3 min-w-0">
                <h4 className="text-white font-semibold">Job #{selectedJobId}</h4>
                {selectedJob && (
                    <>
                        <Badge variant={badgeVariantForJobStatus(selectedJob.status)}>{selectedJob.status}</Badge>
                        {workerLivenessLabel && (
                            <Badge variant={badgeVariantForWorkerLiveness(selectedJob.workerLivenessStatus)}>{workerLivenessLabel}</Badge>
                        )}
                        <Badge variant={badgeVariantForOutcomeMetric(selectedJob.outcomeMetric)}>
                            {labelForOutcomeMetric(selectedJob.outcomeMetric)}
                        </Badge>
                        {selectedJob.outcomeMetric === 'option_profit_percent' && (
                            <Badge title={helpTextForOptionObjective(selectedJob.optionObjective)} variant="secondary">
                                {labelForOptionObjective(selectedJob.optionObjective)}
                            </Badge>
                        )}
                        {selectedJob.optimizerDatasetId != null && (
                            <span className="text-zinc-500 text-xs">Training dataset {selectedJob.optimizerDatasetId}</span>
                        )}
                        {selectedJob.validationDatasetId != null && (
                            <span className="text-zinc-500 text-xs">Walk-forward dataset {selectedJob.validationDatasetId}</span>
                        )}
                    </>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                <div title={auditDisabled && !auditPackPending ? 'Available once the selected job has completed.' : undefined}>
                    <Dropdown
                        closeOnItemClick
                        buttonClassName="border border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
                        buttonContent={auditPackPending ? 'Auditing…' : 'Robust audit'}
                        disabled={auditDisabled}
                        menuClassName="w-80"
                        position="right"
                    >
                        <div className="px-3 py-2 text-[11px] leading-snug text-zinc-500 border-b border-zinc-800">
                            <p>
                                A job ranks rules by raw score, but the leaders can be fragile: profit concentrated in one entity, illiquid
                                names, or incoherent condition stacks. Robust audit re-scores the top results for holdout breadth,
                                concentration, liquidity, and coherence, grades each (excellent → reject), and shows which pass the
                                promotion gates.
                            </p>
                            <p className="mt-1.5">
                                Run it when reviewing a completed job yourself, before trusting or promoting a rule.{' '}
                                <span className="text-zinc-400">Generate Recommendation</span> automatically uses the profile-neutral
                                General audit. These manual profiles are optional comparisons and do not affect AI generation.
                            </p>
                        </div>
                        {AUDIT_PROFILE_OPTIONS.map(({ profile, label, description }) => (
                            <button
                                className="w-full px-3 py-2 text-left hover:bg-zinc-800 transition-colors"
                                key={profile}
                                role="menuitem"
                                type="button"
                                onClick={() => onRunAudit(profile)}
                            >
                                <div className="text-sm text-zinc-200">{label}</div>
                                <div className="text-[11px] leading-snug text-zinc-500">{description}</div>
                            </button>
                        ))}
                    </Dropdown>
                </div>
                <button
                    aria-label="Refresh job data"
                    className="rounded-md border border-zinc-700 bg-zinc-800 p-2.5 text-zinc-300 hover:bg-zinc-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={isRefreshing}
                    title="Refresh job data"
                    type="button"
                    onClick={onRefresh}
                >
                    <FaSyncAlt className={isRefreshing ? 'animate-spin' : undefined} size={12} />
                </button>
            </div>
        </div>
    );
};

export default JobDetailHeader;
